import type { ReactNode } from "react";
import { Link } from "react-router";
import { cx } from "../ui/cx";
import { Icon, type IconName } from "../ui/Icon";

/** Rows in one card with hairlines between (`.k-gl`), the Settings and Mail rhythm. Its children are `ListRow`s. */
export function GroupedList({
  children,
  noLead = false,
  className,
  id,
  label,
}: {
  children: ReactNode;
  /** The rows have no icon or face at the left, so the hairlines start at the card's edge. */
  noLead?: boolean;
  className?: string;
  id?: string;
  label?: string;
}) {
  return (
    <ul className={cx("k-gl", noLead && "k-nolead", className)} id={id} aria-label={label}>
      {children}
    </ul>
  );
}

interface ListRowProps {
  /** An icon in a tile at the left. */
  icon?: IconName;
  /** `danger` draws the icon and the words red, for what cannot be undone. */
  tone?: "wine" | "brass" | "good" | "danger";
  /** Anything else at the left: a face, a ring, a number. */
  lead?: ReactNode;
  title: ReactNode;
  /** One quiet line under the title. */
  sub?: ReactNode;
  /** The quiet line may take two lines instead of one. */
  clamp?: boolean;
  /** Quiet words at the right ("Tue"). */
  value?: ReactNode;
  /** Something at the right before the arrow: a pill, a stack of faces. */
  end?: ReactNode;
  /** The one open beside the list (the iPad on its side). */
  on?: boolean;
  unread?: boolean;
  className?: string;
  /** Leave the arrow out of a row that goes somewhere, as People's rows do. */
  noChevron?: boolean;
  /** Opens a screen of the app. */
  to?: string;
  /** Opens a page outside the app, in a new tab. */
  href?: string;
  onClick?: () => void;
  /** More on the row's own button or link: the page's data-* hooks, aria-label. */
  innerProps?: Record<string, string>;
}

/** One row of a `GroupedList`: a lead, the title with a line under it, a value, and an arrow when it goes somewhere. */
export function ListRow({ icon, tone, lead, title, sub, clamp, value, end, noChevron, on, unread, className, to, href, onClick, innerProps }: ListRowProps) {
  const goes = !!(to || href || onClick);
  const inner = (
    <>
      {icon ? (
        <span className={cx("k-ico", tone !== "danger" && tone)}>
          <Icon name={icon} />
        </span>
      ) : null}
      {lead}
      <span className="k-lr-t">
        <b>{title}</b>
        {sub ? <span className={clamp ? "k-clamp" : undefined}>{sub}</span> : null}
      </span>
      {value ? <span className="k-lr-v">{value}</span> : null}
      {end}
      {goes && !noChevron ? <Icon name={href ? "ext" : "chev"} /> : null}
    </>
  );
  let body: ReactNode;
  if (to) {
    body = (
      <Link className="k-lr-b" to={to} {...innerProps}>
        {inner}
      </Link>
    );
  } else if (href) {
    body = (
      <a className="k-lr-b" href={href} target="_blank" rel="noreferrer" {...innerProps}>
        {inner}
      </a>
    );
  } else if (onClick) {
    body = (
      <button type="button" className="k-lr-b" onClick={onClick} {...innerProps}>
        {inner}
      </button>
    );
  } else {
    body = (
      <div className="k-lr-b" {...innerProps}>
        {inner}
      </div>
    );
  }
  return <li className={cx("k-lr", on && "k-on", unread && "k-unread", tone === "danger" && "k-danger", className)}>{body}</li>;
}
